/**
 * Hook for subscribing to case metadata stored on the active workflow service.
 *
 * Follows the same snapshot + event subscription pattern as useStrategyState.
 * Returns null until the workflow service has been initialised and metadata set.
 */

import { useState, useEffect } from 'react';

import { useVisualization } from '@/context';
import type { CaseMetadata } from '@/visualization';

export function useCaseMetadata(): CaseMetadata | null {
    const { getActiveWorkflowService, serviceManager } = useVisualization();
    const [metadata, setMetadata] = useState<CaseMetadata | null>(() => {
        const service = getActiveWorkflowService();
        return service?.getCaseMetadata() ?? null;
    });

    useEffect(() => {
        const service = getActiveWorkflowService();
        if (!service) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn(
                    '[useCaseMetadata] No active workflow service found. ' +
                        'Ensure this hook is used within a rendered viewer context.'
                );
            }
            return;
        }

        // Sync current value
        setMetadata(service.getCaseMetadata());

        // Subscribe to future updates
        const unsub = service.subscribe(service.EVENTS.CASE_METADATA_SET, (payload: CaseMetadata) => {
            setMetadata(payload);
        });

        return unsub;
    }, [getActiveWorkflowService, serviceManager]);

    return metadata;
}
